import { Control, useWatch } from 'react-hook-form';
import { cn } from '../../../../lib/utils/cn';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../../ui/components/ui/form';
import { Input } from '../../../../ui/components/ui/input';
import { Switch } from '../../../../ui/components/ui/switch';
import { Textarea } from '../../../../ui/components/ui/textarea';
import { formatTermMonths } from '../../lib/billing-term';
import { formatCents, parseEuroInputToCents } from '../../lib/money';
import { ProductFormValues } from '../product-form-dialog/product-form.schema';

export interface BillingTermPriceRowProps {
  control: Control<ProductFormValues>;
  index: number;
  months: number;
}

/** One billing term inside the product form: toggle + monthly net price + optional conditions. */
const BillingTermPriceRow = ({ control, index, months }: BillingTermPriceRowProps) => {
  const isEnabled = useWatch({ control, name: `prices.${index}.isEnabled` });
  const monthlyPrice = useWatch({ control, name: `prices.${index}.monthlyPrice` });
  const previewCents = parseEuroInputToCents(monthlyPrice);

  return (
    <div className={cn('rounded-xl border border-[#E4E7EC] p-4 flex flex-col gap-4', !isEnabled && 'bg-[#F9FAFB]')}>
      <FormField
        control={control}
        name={`prices.${index}.isEnabled`}
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between">
              <FormLabel className={cn('text-[14px] font-bold', isEnabled ? 'text-[#12282A]' : 'text-[#888]')}>{formatTermMonths(months)}</FormLabel>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
            </div>
          </FormItem>
        )}
      />

      {isEnabled && (
        <>
          <FormField
            control={control}
            name={`prices.${index}.monthlyPrice`}
            render={({ field }) => (
              <FormItem className="flex flex-col gap-[10px]">
                <FormLabel className="text-[14px] font-bold text-[#12282A]">Preis netto pro Monat in € *</FormLabel>
                <FormControl>
                  <Input {...field} inputMode="decimal" placeholder="z. B. 499,00" />
                </FormControl>
                {previewCents !== null && <p className="text-[12px] text-[#888]">{formatCents(previewCents)} netto / Monat</p>}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={`prices.${index}.additionalConditions`}
            render={({ field }) => (
              <FormItem className="flex flex-col gap-[10px]">
                <FormLabel className="text-[14px] font-bold text-[#12282A]">Zusätzliche Konditionen</FormLabel>
                <FormControl>
                  <Textarea {...field} placeholder="optional, z. B. einmalige Set-Up-Gebühr" className="min-h-16" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </div>
  );
};

export default BillingTermPriceRow;
